/**
 * Der Pfad im Kopf der Objektansicht (Spec 02a, „Bildschirm Objekt").
 *
 * `path` enthält nur **sichtbare** Vorfahren, von der Wurzel bis zum
 * Elternobjekt — der Server rechnet das aus (`ObjectHead.path`), diese
 * Komponente zeigt es nur an. Für einen Nutzer, dessen Grant erst am
 * Gebäude greift, ist der Pfad leer; die Komponente rendert dann ein leeres
 * `<nav>` statt gar nichts, damit `object-path` in beiden Fällen im
 * Dokument steht.
 */

import { Link } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import type { ObjectHead } from '@/features/objects/api'

interface ObjectPathProps {
  readonly path: ObjectHead['path']
}

export function ObjectPath({ path }: ObjectPathProps) {
  const pathRef = useRef<HTMLOListElement>(null)
  const [isCondensed, setIsCondensed] = useState(false)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    const element = pathRef.current
    if (element === null) return
    const update = () => {
      if (showAll) return
      setIsCondensed(element.scrollWidth > element.clientWidth)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [path, showAll])

  const first = path[0]
  const last = path[path.length - 1]
  const visiblePath = isCondensed && !showAll && path.length > 2 && first !== undefined && last !== undefined ? [first, last] : path

  return (
    // Rahmen und Abstand nur, wenn es etwas zu zeigen gibt. Bei leerem `path`
    // — jedes Grundstück, und jeder Nutzer mit Grant unterhalb der Wurzel —
    // stand hier sonst ein 16 px hoher Streifen mit voller Trennlinie und
    // ohne Inhalt. Das `<nav>` selbst bleibt in beiden Fällen im Dokument,
    // damit `object-path` immer auffindbar ist (siehe Kopfkommentar).
    <nav
      aria-label="Pfad"
      data-testid="object-path"
      className={path.length > 0 ? 'border-border border-b px-(--page-x) py-2' : undefined}
    >
      {/* Scrollt waagerecht statt umzubrechen, damit der Titel darunter auf
       * einer Zeile bleibt (Spec 02a, „Schmale Ansicht"). Der Trenner steht
       * im selben `<li>` wie der Link, nicht daneben — sonst wäre er kein
       * gültiges Kind eines `<ol>`. */}
      <ol ref={pathRef} className="text-label text-muted flex min-w-0 items-center gap-1 overflow-hidden whitespace-nowrap">
        {visiblePath.map((entry, index) => (
          <li key={entry.id} className="flex items-center gap-1">
            {index > 0 && <span aria-hidden="true">›</span>}
            {isCondensed && !showAll && index === 1 && (
              <button
                type="button"
                data-testid="object-path-more"
                aria-label="Vorherige Vorfahren anzeigen"
                onClick={() => { setShowAll(true) }}
                className="hover:text-text inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center"
              >
                …
              </button>
            )}
            <Link
              to="/objekt/$entityId"
              params={{ entityId: entry.id }}
              search={{ tab: undefined }}
              data-testid={`object-path-${entry.id}`}
              // `--tap-min` in beide Richtungen: Eine kurze Bezeichnung („4a")
              // ergab sonst ein 41 px breites Ziel — die Höhe stimmte über die
              // Zeilenhöhe, die Breite fiel unter die harte Regel des
              // `design-system`. Gefunden von `assertTapTargets` in der
              // Journey 02a, nicht im Entwurf.
              className="hover:text-text inline-flex min-h-(--tap-min) min-w-(--tap-min) shrink-0 items-center justify-center transition-colors duration-(--duration-fast) ease-(--ease-out)"
            >
              {entry.label}
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  )
}
